"use client";

import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { defaultTo, get } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocale } from "use-intl";

import type { LinkTarget } from "@/components/link";
import { useLists } from "@/contexts/lists-context";
import { useVideoPlayer } from "@/contexts/video-player-context";
import { WORK_SURFACE } from "@/facets/config";
import { useFacetFilters } from "@/facets/use-facet-filters";
import { useDeferredResourceQuery } from "@/hooks/use-deferred-resource";
import { useRecordView } from "@/hooks/use-record-view";
import { useReleaseFilterOptions } from "@/hooks/use-release-filter-options";
import { useTopLoaderOnRefetch } from "@/hooks/use-top-loader-on-refetch";
import {
  useAutoPlayFromSearchParam,
  useSyncPlayingTrackSearchParam,
} from "@/hooks/use-track-playback-search-params";
import { useTrackVideoPlayerControls } from "@/hooks/use-track-video-player-controls";
import { useTrackVideoPlaylist } from "@/hooks/use-track-video-playlist";
import {
  WORK_RELEASE_FACET_URL_CONFIG,
  buildFacetedSearchState,
  writeFacetedSearchState,
} from "@/lib/faceted-search-url";
import { buildFilterLinkTarget } from "@/lib/filter-url-builder";
import {
  getEffectiveWorkReleaseFormat,
  getWorkReleaseFormatMode,
  getWorkFormatCounts,
  getWorkReleaseFilter,
  getWorkReleaseFormatLinkParam,
  normalizeWorkReleaseFormatMode,
} from "@/lib/work-release-format";
import { getWorkQueryOptions } from "@/routes/_app/work/$id/work-query";
import {
  searchWorkReleasesQueryOptions,
  WORK_RELEASE_PAGE_SIZE,
} from "@/routes/_app/work/$id/work-releases-query";
import { getWorkTrackVideosQueryOptions } from "@/routes/_app/work/$id/work-track-videos-query";
import { orpc } from "@/utils/orpc.client";
import type { FacetedSearchState } from "@garimpa/lib/searchState";

// Use getRouteApi (not the route import) to avoid circular deps between the
// route file and this hook.
const routeApi = getRouteApi("/_app/work/$id");

function shouldEnableWorkReleaseQuery(selectedFormatMode: string, hasWork: boolean): boolean {
  return [selectedFormatMode !== "auto", hasWork].some(Boolean);
}

function isBackgroundRefetch(isFetching: boolean, isLoading: boolean): boolean {
  return isFetching && !isLoading;
}

export function useWorkContentController({ id }: { id: string }) {
  const locale = useLocale();
  const { currentTrack } = useVideoPlayer();
  const { isInCollection, isInWishlist } = useLists();

  useRecordView("work", id);

  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const facetState = buildFacetedSearchState(search, WORK_RELEASE_FACET_URL_CONFIG);

  const onSearchStateChange = useCallback(
    (next: FacetedSearchState | undefined) => {
      navigate({
        search: (prev) => writeFacetedSearchState(prev, next, WORK_RELEASE_FACET_URL_CONFIG),
        replace: false,
        resetScroll: false,
      });
    },
    [navigate],
  );

  const facetControls = useFacetFilters({
    state: facetState,
    onChange: onSearchStateChange,
    surface: WORK_SURFACE,
  });

  const workQuery = useQuery(getWorkQueryOptions(id, locale));
  const work = workQuery.data;
  const selectedFormatMode = getWorkReleaseFormatMode({ mode: search.format });
  const mediaCategoryCounts = get(work, "mediaCategoryCounts");
  const selectedFormat = getEffectiveWorkReleaseFormat({
    mode: selectedFormatMode,
    mediaCategoryCounts,
  });
  const selectedSearch = defaultTo(search.q, null);
  const playing = defaultTo(search.playing, null);
  const releasePage = defaultTo(search.page, 1);

  const handleFormatChange = useCallback(
    (value: string) => {
      const nextFormatMode = normalizeWorkReleaseFormatMode(value);
      navigate({
        search: (prev) => ({
          ...prev,
          format: nextFormatMode !== "auto" ? nextFormatMode : undefined,
          formatSet: undefined,
          page: undefined,
        }),
        replace: false,
        resetScroll: false,
      });
    },
    [navigate],
  );
  const getFormatLinkTarget = useCallback(
    (value: string) => {
      const format = getWorkReleaseFormatLinkParam({ value, selectedFormat });
      return buildFilterLinkTarget({
        to: "/work/$id",
        params: { id },
        currentSearch: { ...search, formatSet: undefined },
        target: format
          ? { key: "format", kind: "param", value: format }
          : { key: "format", kind: "param", mode: "clear" },
      });
    },
    [id, search, selectedFormat],
  );

  const setPlaying = useCallback(
    (value: string | null) => {
      navigate({
        search: (prev) => ({ ...prev, playing: value ?? undefined }),
        replace: false,
        resetScroll: false,
      });
    },
    [navigate],
  );

  const computeTrackVideosResource = useCallback(
    () => orpc.music.computeTrackVideosResource.call({ entity: "work", id }),
    [id],
  );
  const {
    query: trackVideosQuery,
    resource: trackVideosResource,
    value: trackVideosValue,
  } = useDeferredResourceQuery({
    queryOptions: getWorkTrackVideosQueryOptions(id),
    compute: computeTrackVideosResource,
  });

  const releaseQueryInput = {
    id,
    filter: getWorkReleaseFilter({
      filter: facetControls.filter,
      mode: selectedFormatMode,
      mediaCategoryCounts,
    }),
    sort: facetControls.sort,
    search: selectedSearch,
  };
  const releasesQueryEnabled = shouldEnableWorkReleaseQuery(selectedFormatMode, Boolean(work));
  const releasesQuery = useQuery({
    ...searchWorkReleasesQueryOptions({ ...releaseQueryInput, page: releasePage }),
    enabled: releasesQueryEnabled,
    placeholderData: keepPreviousData,
  });
  useTopLoaderOnRefetch(isBackgroundRefetch(releasesQuery.isFetching, releasesQuery.isLoading));

  const handleSearchChange = useCallback(
    (value: string | null) => {
      navigate({
        search: (prev) => ({ ...prev, q: value ?? undefined, page: undefined }),
        replace: false,
        resetScroll: false,
      });
    },
    [navigate],
  );

  const releasesData = releasesQuery.data;
  const releases = defaultTo(get(releasesData, "items"), []);
  const facets = get(releasesData, "facets");
  const releaseResultCount = defaultTo(get(releasesData, "found"), 0);
  const releasePageCount = Math.max(1, Math.ceil(releaseResultCount / WORK_RELEASE_PAGE_SIZE));

  // Warm the next page as soon as the current one settles, so "next" usually
  // swaps instantly instead of dimming the grid while it loads.
  const queryClient = useQueryClient();
  const nextReleasePageOptions =
    releasesQueryEnabled && releasesData && releasePage < releasePageCount
      ? searchWorkReleasesQueryOptions({ ...releaseQueryInput, page: releasePage + 1 })
      : null;
  const nextReleasePageKey = nextReleasePageOptions
    ? JSON.stringify(nextReleasePageOptions.queryKey)
    : null;
  const nextReleasePageOptionsRef = useRef(nextReleasePageOptions);
  nextReleasePageOptionsRef.current = nextReleasePageOptions;
  const isReleasesFetching = releasesQuery.isFetching;
  useEffect(() => {
    // The serialized key changes whenever any input of the next-page query does.
    const options = nextReleasePageOptionsRef.current;
    if (!nextReleasePageKey || !options || isReleasesFetching) return;
    void queryClient.prefetchQuery(options);
  }, [isReleasesFetching, nextReleasePageKey, queryClient]);

  const getReleasePageLinkTarget = useCallback(
    (page: number): LinkTarget => {
      const nextPage = Math.min(Math.max(1, page), releasePageCount);
      return {
        to: "/work/$id",
        params: { id },
        search: {
          ...search,
          page: nextPage > 1 ? nextPage : undefined,
        },
      };
    },
    [id, releasePageCount, search],
  );

  useEffect(() => {
    if (!releasesData || releasePage <= releasePageCount) return;

    navigate({
      search: (prev) => ({ ...prev, page: releasePageCount > 1 ? releasePageCount : undefined }),
      replace: true,
      resetScroll: false,
    });
  }, [navigate, releasePage, releasePageCount, releasesData]);

  const formatCounts = getWorkFormatCounts({
    mediaCategoryCounts,
    totalCount: get(work, "releaseCount"),
  });

  const filterOptions = useReleaseFilterOptions(facets, locale);

  const workMedia = defaultTo(get(workQuery.data, "media"), []);
  const trackVideos = defaultTo(trackVideosValue, []);
  const isPlayAllResolving = [
    trackVideosQuery.isLoading,
    get(trackVideosResource, "status") === "pending",
  ].some(Boolean);
  const workArtistName = defaultTo(get(workQuery.data, "artistDisplay"), null);
  const videoPlaylist = useTrackVideoPlaylist(workMedia, trackVideos);
  const workTitle = get(work, "title");
  const workSource = useMemo(
    () => (workTitle ? { title: workTitle, url: `/work/${id}#work-tracks` } : null),
    [workTitle, id],
  );
  const {
    playAll: handlePlayAll,
    playByYoutubeId: handlePlayTrack,
    hasPlayableTracks,
  } = useTrackVideoPlayerControls({
    playlist: videoPlaylist,
    artistName: workArtistName,
    source: workSource,
  });

  useSyncPlayingTrackSearchParam({
    playing,
    setPlaying,
    currentYoutubeId: get(currentTrack, "youtubeId"),
  });

  useAutoPlayFromSearchParam({
    playing,
    playlist: videoPlaylist,
    onPlayByYoutubeId: handlePlayTrack,
  });

  return {
    work: work!,
    isInCollection,
    isInWishlist,
    releases,
    facetControls,
    selectedFormat,
    selectedFormatMode,
    selectedSearch,
    handleSearchChange,
    handleFormatChange,
    getFormatLinkTarget,
    releasePage,
    releasePageCount,
    releaseResultCount,
    isReleasesFetching: releasesQuery.isFetching,
    getReleasePageLinkTarget,
    formatCounts,
    filterOptions,
    hasPlayableTracks,
    isPlayAllResolving,
    handlePlayAll,
    handlePlayTrack,
    workMedia,
    trackVideos,
  };
}
